import { useQuery } from '@tanstack/react-query';
import type { FatiaDoRelatorio } from '@central/contracts';
import { formatarDataHora } from '@/lib/format';
import {
  Carregando,
  Contagem,
  Falhou,
  Numero,
  Painel,
  type ColunaDeTabela,
} from '@/features/relatorios/componentes';
import { useFiltro } from '@/features/relatorios/filtros';
import { GraficoDeBarras } from '@/features/relatorios/graficos';
import { chavesDeRelatorio, obterBacklog } from '@/features/relatorios/relatorios.api';

/**
 * Backlog — o que está aberto **agora**.
 *
 * É a única aba em que o filtro de datas não muda o número, e a tela diz isso
 * em vez de deixar a pessoa descobrir mexendo. "O que está aberto" não tem data
 * de início: filtrá-lo por período responderia outra pergunta — "o que abriu em
 * agosto e ainda está aberto" — e ninguém notaria a troca.
 */

function colunas(total: number): ReadonlyArray<ColunaDeTabela<FatiaDoRelatorio>> {
  return [
    { cabecalho: 'Item', celula: (l) => l.rotulo, csv: (l) => l.rotulo },
    {
      cabecalho: 'Abertos',
      alinharADireita: true,
      celula: (l) => l.total.toLocaleString('pt-BR'),
      csv: (l) => l.total,
    },
    {
      cabecalho: 'Participação',
      alinharADireita: true,
      celula: (l) => (total === 0 ? '—' : `${((l.total / total) * 100).toFixed(1).replace('.', ',')}%`),
      csv: (l) => (total === 0 ? '' : ((l.total / total) * 100).toFixed(1).replace('.', ',')),
    },
  ];
}

const somaDe = (fatias: readonly FatiaDoRelatorio[]) =>
  fatias.reduce((soma, fatia) => soma + fatia.total, 0);

const paraBarra = (fatia: FatiaDoRelatorio) => ({ rotulo: fatia.rotulo, total: fatia.total });

const dias = (valor: number) =>
  `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`;

export function BacklogPage() {
  const { consulta } = useFiltro();
  const relatorio = useQuery({
    queryKey: chavesDeRelatorio.de('backlog', consulta),
    queryFn: () => obterBacklog(consulta),
  });

  if (relatorio.isPending) return <Carregando />;
  if (relatorio.isError) {
    return <Falhou erro={relatorio.error} aoTentar={() => void relatorio.refetch()} />;
  }

  const dados = relatorio.data;

  return (
    <div className="animate-slide-up space-y-6">
      <p className="text-xs text-muted-foreground">
        Foto de {formatarDataHora(dados.apuradoEm)}. O filtro de datas não altera esta aba — só
        equipe, organização e tipo.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Contagem rotulo="Chamados abertos" valor={dados.total} />
        <Numero
          rotulo="Idade média"
          indicador={dados.idadeMediaDias}
          formatar={dias}
          ajuda="Desde a abertura, em dias corridos."
        />
        <Numero
          rotulo="Mais antigo"
          indicador={dados.idadeDoMaisAntigoDias}
          formatar={dias}
          tom="atencao"
        />
        <Contagem
          rotulo="Com prazo vencido"
          valor={dados.vencidos}
          tom={dados.vencidos > 0 ? 'ruim' : 'bom'}
        />
        <Contagem
          rotulo="Em risco"
          valor={dados.emRisco}
          tom={dados.emRisco > 0 ? 'atencao' : 'bom'}
          ajuda="Passou do limiar de aviso e ainda não venceu."
        />
      </div>

      <Painel
        titulo="Idade do backlog"
        descricao="A cauda à direita é o que ninguém está olhando. Uma fila saudável é alta à esquerda e curta à direita."
        indicador={dados.porFaixaDeIdade}
        grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} nome="Abertos" />}
        colunas={(linhas) => colunas(somaDe(linhas))}
        chaveDe={(linha) => linha.chave}
        nome={['relatorio', 'backlog', 'por-idade']}
        recorte={dados.recorte}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <Painel
          titulo="Por equipe"
          indicador={dados.porEquipe}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} nome="Abertos" />}
          colunas={(linhas) => colunas(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'backlog', 'por-equipe']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por situação"
          indicador={dados.porSituacao}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} nome="Abertos" />}
          colunas={(linhas) => colunas(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'backlog', 'por-situacao']}
          recorte={dados.recorte}
        />
        <Painel
          titulo="Por organização"
          indicador={dados.porOrganizacao}
          grafico={(linhas) => <GraficoDeBarras dados={linhas.map(paraBarra)} nome="Abertos" />}
          colunas={(linhas) => colunas(somaDe(linhas))}
          chaveDe={(linha) => linha.chave}
          nome={['relatorio', 'backlog', 'por-organizacao']}
          recorte={dados.recorte}
        />
      </div>
    </div>
  );
}
